import React, {
  useState,
} from 'react';
import {
  injectStripe,
} from 'react-stripe-elements';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  Button,
  AddCardForm,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import * as S from '../styled';

const mapState = state => ({
  defaultCardId: coreSelectors.defaultCardIdSelector(state),
  paymentMethods: coreSelectors.getResourceMappedList(
    'paymentMethods',
  )(state),
});

type Props = {
  stripe: any,
  planId: number,
  isUpgrade?: boolean,
  clientSecret: string,
  onError?: { (): void },
  onSuccess?: { (): void },
};

const Buy = ({
  stripe,
  planId,
  isUpgrade,
  clientSecret,
  onError,
  onSuccess,
}: Props) => {
  const {
    defaultCardId,
    paymentMethods,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const [errorMsg, setError] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [cardFormIsVisible, setCardFormVisible] = useState(false);

  const onSetDefaultPaymentMethod = (method) => {
    dispatch(
      coreActions.requestSetDefaultPaymentMethod({
        id: method.id,
      }),
    );
  };

  const handleAddCard = (res, actions) => {
    dispatch(
      coreActions.requestCreatePaymentMethod({
        body: {
          id: res.paymentMethod.id,
        },
        callbacks: {
          ...actions,
          onSuccess: () => {
            setCardFormVisible(false);
          },
        },
      }),
    );
  };

  const onUpgrade = () => {
    setError(null);
    setLoading(true);

    dispatch(
      coreActions.requestUpgradeAccount({
        endpoint: 'upgrade',
        callbacks: {
          onSuccess: (r) => {
            setLoading(false);
            onSuccess(r);
          },
        },
      }),
    );
  };

  const onSubscribeProduct = () => {
    setError(null);
    setLoading(true);
    const setPlanSuccess = (r) => {
      dispatch(
        coreActions.requestFetchClientEntity({
          list: 'library',
          resourceType: 'personalWorkouts',
        }),
      );
      dispatch(
        coreActions.requestFetchClientEntity({
          list: 'library',
          resourceType: 'personalPrograms',
        }),
      );
      setLoading(false);
      onSuccess(r);
    };
    dispatch(
      coreActions.requestSetPlan({
        data: {
          product_plan_id: planId,
        },
        callbacks: {
          onError: (e) => {
            // setError(error.message);
            onError(e);
            setLoading(false);
          },
          onSuccess: setPlanSuccess,
        },
      }),
    );
  };

  const onBuy = async () => {
    setError(null);
    setLoading(true);
    const method = paymentMethods.find(
      m => m.id === defaultCardId,
    ) || paymentMethods[0];
    const {
      error,
      paymentIntent,
    } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: method?.id,
      },
    );

    setLoading(false);
    if (error) {
      setError(error.message);
      onError(error);
      console.log('error', error);
    } else {
      onSuccess(paymentIntent);
    }
  };

  const buttonText = planId ? 'Subscribe' : 'Buy Now';

  return (
    <>
      <Flex width="100%" column>
        <Text>
          Pay with card
        </Text>
        <Flex
          column
          width="100%"
        >
          {paymentMethods.map(m => (
            <Flex
              key={m.id}
              width="100%"
              align="center"
              spacing={5}
              clickable
              onClick={() => onSetDefaultPaymentMethod(m)}
            >
              <S.CheckCircle
                isDefault={m.id === defaultCardId}
              />
              <Text
                size="13px"
                color="#8e8e8e"
              >
                ending in
                {' '}
                {m.card.last4}
              </Text>
            </Flex>
          ))}
        </Flex>
        {cardFormIsVisible ? (
          <Flex
            width="100%"
            padding="10px 0"
            column
          >
            <hr />
            <AddCardForm
              submitText="Finish"
              onCancel={() => setCardFormVisible(false)}
              onSuccess={handleAddCard}
            />
            <hr />
          </Flex>
        ) : (
          <S.LinkButton onClick={() => setCardFormVisible(true)}>
            +Add new card
          </S.LinkButton>
        )}
      </Flex>
      {errorMsg && (
        <Flex
          width="100%"
          justify="center"
          margin="15px 0"
        >
          <Text
            color="#b64135"
          >
            {errorMsg}
          </Text>
        </Flex>
      )}
      <Flex
        width="100%"
        justify="center"
        margin="15px 0"
      >
        <Button
          ghost
          appearance="primary"
          isLoading={isLoading}
          isDisabled={paymentMethods.length < 1}
          onClick={() => {
            if (isUpgrade) onUpgrade();
            else if (planId) onSubscribeProduct();
            else onBuy();
          }}
        >
          {buttonText}
        </Button>
      </Flex>
      <Flex width="100%">
        <Text
          size="13px"
          color="#c3c3c3"
        >
          *Upon clicking
          {' '}
          {buttonText}
          , your card will be charged
          and your account will be updated.
        </Text>
      </Flex>
    </>
  );
};

Buy.defaultProps = {
  isUpgrade: false,
  onError: () => {},
  onSuccess: () => {},
};

export default injectStripe(Buy);
